import '../product-card/product-card-display';
import debounce from 'lodash/debounce';
import emit from '../../common/events';
import store from '../../store/store';
import { BaseComponent } from '../base-component/base-component';
import { Catalog } from '../../common/constants/catalog';
import { Modal } from '../modal/modal';
import { SearchFacet, SearchFacetValue } from './interfaces/search.interface';
import { StoreState } from '../../store/store.interface';
import { booleanConverter } from '../../common/converters';
import { connect } from 'pwa-helpers';
import { customElement, property, state } from 'lit/decorators.js';
import { getSearchRequest, performSearch } from './thunks/search.thunk';
import { getUserDataForUserAnalytics } from '../user-profile/user-profile.selectors';
import { html, nothing } from 'lit';
import { isFacetPreFiltered } from './utils/predefined-filter.util';
import { repeat } from 'lit/directives/repeat.js';
import {
  searchParam,
  selectFacets,
  selectSelectedCatalog,
  selectedFacetCount,
  selectTotal,
  selectSearchPageType,
} from './store/search.selector';
import { selectRequestFacetValues } from './store/search.selector';
import { setLoading } from './store/search.store';
import { findElementInContexts } from './utils/search.util';
import { ifDefined } from 'lit-html/directives/if-defined.js';

/**
 * The default order configuration array for search filters facets.
 */
export const DEFAULT_FACETS_ORDER: Array<string> = [
  'TYPE',
  'PRODUCT_TAG_PATH',
  'PRODUCT_SCHOOL_LEVEL_PATH',
  'PRODUCT_CURRICULUM_FOCUS_PATH',
  'PROGRAM_TITLE',
  'PRODUCT_TYPE_PATH',
  'PRODUCT_FORMAT',
  'PRODUCT_COPYRIGHT_YEAR',
  'SPECIFIC_MARKET',
];

/**
 * An element to display search filters
 *
 * @csspart group
 * @csspart subgroup
 * @csspart header
 * @csspart subheader
 *
 * @event filter-clear-all - A user has clicked the remove all filters button.
 *        <br><br><span style="color: green">&check;</span> dataLayer
 *        <br><span style="color: green">&check;</span> bubbles
 *        <br><br>event.detail: n/a
 * @event filter-add-facet - A user has added a filter
 *        <br><br><span style="color: green">&check;</span> dataLayer
 *        <br><span style="color: green">&check;</span> bubbles
 *        <br><br>event.detail: { group, name, label }
 * @event filter-remove-facet - A user has removed a filter
 *        <br><br><span style="color: green">&check;</span> dataLayer
 *        <br><span style="color: green">&check;</span> bubbles
 *        <br><br>event.detail: { group, name, label }
 * @event filter-remove-facet-pill - A user has removed a filter
 *        <br><br><span style="color: green">&check;</span> dataLayer
 *        <br><span style="color: green">&check;</span> bubbles
 *        <br><br>event.detail: { group, name, label }
 * @event filter-open-modal - A user has opened filter modal
 *        <br><br><span style="color: green">&check;</span> dataLayer
 *        <br><span style="color: green">&check;</span> bubbles
 *        <br><br>event.detail: n/a
 * @event filter-close-modal - A user has closed filter modal
 *        <br><br><span style="color: green">&check;</span> dataLayer
 *        <br><span style="color: green">&check;</span> bubbles
 *        <br><br>event.detail: n/a
 */
@customElement('mhecomm-search-filters')
export class SearchFilters extends connect(store)(BaseComponent) {
  @state()
  private facets?: Array<SearchFacet>;

  @property()
  store?: string;

  /**
   * is plp search within or not
   */
  @property({ converter: booleanConverter })
  plpSearch = false;

  @property()
  categoryId: string | undefined;

  @property({ converter: booleanConverter })
  showFacetHeader = true;

  @property({ converter: booleanConverter })
  collapseFilter = false;

  @property({ converter: booleanConverter })
  tabbedSearchEnabled = false;

  @state()
  private showAriaInModal = false;

  @state()
  private displayProgramsLimit = 10;

  @state()
  private filterModal;

  @state()
  private lastUpdatedFacet: string | null = null;

  private selectedFacetCount;
  private isFilterRenderedInModal = false;
  private appliedFilter: Array<SearchFacet> = [];
  private totalSearchResult = 0;
  private isDefaultCollapsedFilter = true;
  private selectedCatalog;

  private readonly onResize = debounce(() => this.udpateFilterPlacement(), 300);

  renderComp() {
    return html`
      <div tabindex="-1" role="region" aria-label="Filters. Selecting or deselecting a filter will update results and may change the available options in other filters.">
        <span id="filter-announcements" aria-live="polite" class="visually-hidden"></span>
        ${this.isFilterRenderedInModal && this.facets?.length
          ? this.renderFilterModal()
          : html`
              ${this.appliedFilter.length ? this.renderAppliedFilter() : nothing}
              ${this.facets?.length ? this.renderFacetHeader() : nothing} ${this.renderFacets()}
            `}
      </div>
    `;
  }

  renderFacetHeader() {
    if (!this.showFacetHeader) {
      return nothing;
    }

    return html`
      <div class="d-flex d-md-block d-lg-flex justify-content-between align-items-end mb-2">
        <div class="d-flex justify-content-between w-100">
          ${this.totalSearchResult > 0 ? html`<h3 class="h6 mb-0 me-2 fw-bold lh-base">Filter By</h3>` : nothing}
          ${this.appliedFilter?.length > 0 ? nothing : this.renderSkipFIlter()}
        </div>
      </div>
    `;
  }

  renderSkipFIlter() {
    if (this.tabbedSearchEnabled) {
      return html`
        <button
          id="skip-to-results-tab"
          data-automation-id="skip-to-results-tab"
          class="visually-hidden-focusable btn btn-link p-0 border-0"
          @click=${this.skipFilterToTabClick}
        >
          Skip to Results Tab
        </button>
      `;
    }

    return html`
      <button
        id="skip-to-results"
        data-automation-id="skip-to-results"
        class="visually-hidden-focusable btn btn-link p-0 border-0"
        @click=${this.skipFilterClick}
      >
        Skip to Results
      </button>
    `;
  }

  renderFilterModal() {
    const modalContextId = 'filter';
    this.filterModal = new Modal({
      modalContext: {
        componentRef: modalContextId,
        componentRefData: this.analyticDetail(),
      },
      header: 'Filter By',
      body: {
        template: html`
          ${this.showAriaInModal
            ? html`<div class="visually-hidden" aria-live="polite" role="status">
                ${this.totalSearchResult} Results
              </div>`
            : nothing}
          ${this.appliedFilter.length ? this.renderAppliedFilter() : nothing} ${this.renderFacets(true)}
        `,
      },
      disableFooter: true,
    });

    this.filterModal.addEventListener(`${modalContextId}-modal-close`, () => {
      (this.shadowRoot?.querySelector('.filter-by-button') as HTMLInputElement)?.focus();
    });

    return html`
      <div class="text-center">
        <button
          type="button"
          class="btn btn-outline-primary w-100 filter-by-button"
          aria-haspopup="dialog"
          data-bs-toggle="modal"
          data-bs-target="#filterModal"
          aria-label="Open filters. ${this.selectedFacetCount
            ? `${this.selectedFacetCount} filters currently selected.`
            : ''}"
          @click=${this.openModal}
        >
          Filter By ${this.selectedFacetCount ? `(${this.selectedFacetCount})` : ''}
        </button>
      </div>
    `;
  }

  renderFacets(isModal = false) {
    return html`
      ${this.totalSearchResult > 0 ? this.facets?.map(
        (facet, index) => html`
          <div
            class="accordion mb-2 ${this.isFilterRenderedInModal ? 'accordion-flush' : ''}"
            id="${facet.name}-accordion"
            data-automation-id="${facet.name}-accordion"
          >
            <div class="accordion-item ${this.isFilterRenderedInModal ? 'border-primary' : ''}">
              <div class="${ifDefined(this.isFilterRenderedInModal ? 'border-bottom' : undefined)}">
              ${isModal && this.isFilterRenderedInModal
                ? html`<h3 class="accordion-header" id="${facet.name}">
                    ${this.renderFacetAccordionButton(facet, index, isModal)}
                  </h3>`
                : html`<h4 class="accordion-header" id="${facet.name}">
                    ${this.renderFacetAccordionButton(facet, index, isModal)}
                  </h4>`
              }
                <div
                  id="${facet.name}-collapse-${index}"
                  class="accordion-collapse collapse
                  ${this.checkIfDefaultCollapsedFilter() && !isModal ? '' : 'show'} 
                  ${this.isFilterRenderedInModal ? 'px-0 py-2' : 'p-3'}"
                  aria-labelledby="${facet.name}"
                  role="region"
                  data-bs-parent="#${facet.name}-accordion"
                >
                  <fieldset>
                    <legend class="visually-hidden">${facet.label} checkbox</legend>
                    ${this.lastUpdatedFacet === facet.name ? html`
                      <span 
                        role="status" 
                        aria-live="polite" 
                        aria-atomic="true" 
                        class="visually-hidden"
                      >
                        Filters updated. ${facet.label}: ${facet.facetValues.length} filters available.
                      </span>
                    ` : nothing}
                    ${repeat(
                      facet.facetValues,
                      (facetValue) => facetValue.name,
                      (facetValue, facetIndex) => {
                        let collapseClass = '';
                        if (facetIndex >= this.displayProgramsLimit) {
                          collapseClass = `collapse-facet-programs-${facet.name} collapse`;
                        }
                        return this.renderCheckbox(facet, facetValue, collapseClass);
                      }
                    )}
                  </fieldset>
                  ${facet.facetValues.length > this.displayProgramsLimit
                    ? html` <button
                        type="button" 
                        class="btn btn-link p-0 m-0 align-baseline text-decoration-none"
                        aria-haspopup="dialog"
                        id="viewAllFacetProgramsLink-${facet.name}"
                        @click=${(event) => this._handleFacetProgramsCollapseToggle(facet, facet.facetValues, event, `viewAllFacetProgramsLink-${facet.name}`)}
                        >View All
                        ${this.isFilterRenderedInModal
                          ? html`<uxt-icon
                              class="px-2"
                              id="enterDropDownIcon-${facet.name}"
                              icon="chevron-down"
                            ></uxt-icon>`
                          : nothing}
                      </button>`
                    : nothing}
                </div>
              </div>
            </div>
          </div>
        `
      ): nothing}
    `;
  }

  private renderFacetAccordionButton(facet: SearchFacet, index: number, isModal: boolean) {
    return html`
      <button
        class="accordion-button fw-semibold mb-0 shadow-none text-capitalize
        ${this.checkIfDefaultCollapsedFilter() && !isModal ? 'collapsed' : ''}
        ${this.isFilterRenderedInModal ? 'px-0 mb-0' : ''}"
        type="button"
        data-bs-toggle="collapse"
        data-bs-target="#${facet.name}-collapse-${index}"
        aria-expanded="${this.checkIfDefaultCollapsedFilter() && !isModal ? false : true}"
        aria-controls="${facet.name}-collapse-${index}"
        @click=${(e) => this._handleCollapseToggle(e, facet.name)}
      >
        ${facet.label}
      </button>
    `;
  }

  renderCheckbox(facet, value, collapseClass = '', isPerformSearch = true) {
    return html`
      <div class="flex-row pb-2 pe-3 ${collapseClass}">
        <div class="form-check">
          <input
            class="form-check-input"
            type="checkbox"
            .checked=${value.selected}
            value=${value.label}
            data-automation-id="${this.replaceSpaceWithinStr(value.name)}"
            id="${this.replaceSpaceWithinStr(value.name)}"
            @change=${(e: Event) => {
              const checkbox = e.target as HTMLInputElement;
              if (!isPerformSearch) {
                const facetValues = selectRequestFacetValues(store.getState() as StoreState, facet.name);
                if (facetValues) {
                  value = { ...value, selected: facetValues.includes(value.name) };
                }
              }

              const announcer = this.shadowRoot?.getElementById('filter-announcements');
              if (announcer) {
                const action = checkbox.checked ? 'added' : 'removed';
                announcer.textContent = `${value.label} filter ${action}. Search results have been updated.`;
              }

              this.updateFacet(facet.name, value, false, isPerformSearch);
            }}
          />
          <label
            class="form-check-label d-flex justify-content-between"
            for="${this.replaceSpaceWithinStr(value.name)}"
          >
            <span> ${value.label} </span> <span>(${value.count})</span>
          </label>
        </div>
      </div>
    `;
  }

  private _handleFacetProgramsCollapseToggle(facet, facetValues, event, triggerId) {
    if (event && (event.type == 'click' || (event.type == 'keyup' && event.code == 'Space'))) {
      if (this.isFilterRenderedInModal) {

        const viewAllFacetProgramsLink = findElementInContexts<HTMLLinkElement>(
          `#viewAllFacetProgramsLink-${facet.name}`,
          [this.shadowRoot, event.target.getRootNode(), document]
        ) as HTMLLinkElement;

        const dropDownIcon = findElementInContexts<HTMLElement>(
          `#enterDropDownIcon-${facet.name}`,
          [this.shadowRoot, event.target.getRootNode(), document]
        ) as HTMLElement;

        const collapsePrograms = findElementInContexts<HTMLElement>(
          `.collapse-facet-programs-${facet.name}`,
          [this.shadowRoot, event.target.getRootNode(), document],
          true
        ) as NodeListOf<HTMLElement>;
        const collapseProgramClass = `flex-row pb-2 pe-3 collapse-facet-programs-${facet.name} collapse`;
        if (viewAllFacetProgramsLink.getAttribute('aria-expanded') == 'true') {
          viewAllFacetProgramsLink!.firstChild!.textContent = 'View All';
          viewAllFacetProgramsLink.setAttribute('aria-expanded', 'false');
          dropDownIcon?.setAttribute('icon', 'chevron-down');
          collapsePrograms?.forEach((element) => element.setAttribute('class', `${collapseProgramClass}`));
        } else {
          viewAllFacetProgramsLink!.firstChild!.textContent = 'View Less';
          viewAllFacetProgramsLink.setAttribute('aria-expanded', 'true');
          dropDownIcon?.setAttribute('icon', 'chevron-up');
          collapsePrograms?.forEach((element) => element.setAttribute('class', `${collapseProgramClass} show`));
        }
      } else {
        const modal: Modal = new Modal({
          header: facet.label,
          isHeaderTextCapitalize: true,
          body: { template: this._getFacetProgramsTemplate(facet, facetValues) },
          primaryButton: { label: 'Apply' },
          modalContext: { componentRef: `view-all-facet-programs` },
        });
        modal.addEventListener('mhecomm-modal-confirmed', () => {
          store.dispatch(performSearch());
          modal.close();
        });
        modal.addEventListener(`view-all-facet-programs-modal-close`, () => {
          (this.shadowRoot?.querySelector(`#${triggerId}`) as HTMLInputElement)?.focus();
        });
        modal.open();
      }
    }
  }

  private _getFacetProgramsTemplate(facet, facetValues) {
    return html`
      <style>
        #allPrograms {
          max-height: 60vh;
        }
      </style>
      <div id="allPrograms" class="overflow-auto ps-1">
        ${facetValues?.map((facetValue) => this.renderCheckbox(facet, facetValue, '', false))}
      </div>
    `;
  }

  renderAppliedFilter() {
    return html`
      <div class="d-flex justify-content-between w-100">
        <h3 class="h6 fw-bold mb-3">Applied Filters</h3>
        ${this.isFilterRenderedInModal ? nothing : html` <div>${this.renderSkipFIlter()}</div> `}
      </div>
      ${this.appliedFilter?.map(
        (facet) => html` <div class="mb-3">
          <span class="h6 fw-normal">${facet.label}:</span>
          <ul class="list-unstyled">
            ${facet.facetValues?.map(
              (facetValue) => html`
                  <li>
                    <div
                      class="alert alert-secondary alert-dismissible mb-2 py-2 ps-2 d-flex align-items-top"
                    >
                      <div>
                        <p class="mb-0">${facetValue.label}</p>
                        <button
                          data-automation-id="${this.replaceSpaceWithinStr(facetValue.name)}_clear" 
                          id="${this.replaceSpaceWithinStr(facetValue.name)}_clear"
                          type="button"
                          class="btn-close btn-sm pb-2"
                          data-bs-dismiss="alert"
                          aria-label="Remove Filter ${facetValue.label}"
                          @click=${() => {
                            const announcer = this.shadowRoot?.getElementById('filter-announcements');
                            if (announcer) {
                              announcer.textContent = `${facetValue.label} filter removed. Search results have been updated.`;
                            }
                            this.updateFacet(facet.name, facetValue, true);
                          }}
                        ></button>
                      </div>
                    </div>
                    </li>
                  </div>
                `
            )}
          </ul>
        </div>`
      )}
      ${this.selectedFacetCount
        ? html`
            <button
              class="btn btn-link p-0 mb-3"
              @click=${() => {
                const announcer = this.shadowRoot?.getElementById('filter-announcements');
                if (announcer) {
                  announcer.textContent = 'All filters cleared';
                }
                this.clearAllFilters();
              }}
              aria-label="Clear All Filters"
              id="clear_filters"
              data-automation-id="clear_filters"
            >
              Clear Filters
            </button>
          `
        : nothing}
    `;
  }

  _handleCollapseToggle(e: PointerEvent, name: string) {
    const element = e.target as HTMLElement;
    const collapseId = element.getAttribute('data-bs-target') as string;
    
    if (!collapseId) {
      return;
    }
    
    // Try shadowRoot first, then fallback to rootNode
    let collapse = this.shadowRoot?.querySelector(collapseId) as HTMLElement;
    let searchContext: Document | ShadowRoot | null = this.shadowRoot;

    if (!collapse) {
      const rootNode = element.getRootNode();
      if (rootNode instanceof Document || rootNode instanceof ShadowRoot) {
        collapse = rootNode.querySelector(collapseId) as HTMLElement;
        searchContext = rootNode;
      }
    }

    if (!collapse) {
      return;
    }

    const collapseInstance = this.bsInit(collapseId, window.bootstrap?.Collapse, { toggle: false }, searchContext)?.instance;

    if (!collapseInstance) {
      return;
    }

    if (element.classList.contains('collapsed')) {
      collapse.addEventListener('show.bs.collapse', () => element.classList.remove('collapsed'), { once: true });
      collapseInstance.show();
      emit({
        element: this,
        name: 'filter-group-open',
        detail: this.analyticDetail({ name }),
        dataLayer: true,
      });
      element.setAttribute('aria-expanded', 'true');
    } else {
      collapse.addEventListener('hide.bs.collapse', () => element.classList.add('collapsed'), { once: true });
      collapseInstance.hide();
      emit({
        element: this,
        name: 'filter-group-close',
        detail: this.analyticDetail({ name }),
        dataLayer: true,
      });
      element.setAttribute('aria-expanded', 'false');
    }
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.addTrackedEventListener(window, 'resize', this.onResize);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
  }

  updated() {
    this.udpateFilterPlacement();
  }

  udpateFilterPlacement() {
    const windowWidth = () => {
      const clientWidth = window.document.documentElement.clientWidth;
      const body = window.document.body;
      return (window.document.compatMode === 'CSS1Compat' && clientWidth) || (body && body.clientWidth) || clientWidth;
    };

    const bool = this.isFilterRenderedInModal;

    if (windowWidth() >= 768) this.isFilterRenderedInModal = false;
    else this.isFilterRenderedInModal = true;

    if (bool != this.isFilterRenderedInModal) this.requestUpdate();
  }

  checkIfDefaultCollapsedFilter() {
    return this.isDefaultCollapsedFilter && this.collapseFilter
  }
  stateChanged(state: StoreState) {
    this.facets = selectFacets(state);
    this.facets = this.facets?.filter((facet, _i) => facet.label !== 'Doc_type' && !isFacetPreFiltered(facet));

    // Set this.selectedCatalog
    this.selectedCatalog = selectSelectedCatalog(state);

    if (this.selectedCatalog === Catalog.seg && this.facets?.length) {
      this.facets.sort((a, b) => DEFAULT_FACETS_ORDER.indexOf(a.name) - DEFAULT_FACETS_ORDER.indexOf(b.name));
      this.updateFacetLabel();
    }

    this.selectedFacetCount = selectedFacetCount(state);
    this.totalSearchResult = selectTotal(state) || 0;
    this.appliedFilter =
      this.facets?.reduce((arr: any, facet) => {
        const selectedFacetValues = facet?.facetValues.filter((facetValue) => facetValue.selected);
        if (selectedFacetValues.length) {
          arr.push({
            ...facet,
            facetValues: selectedFacetValues,
          });
        }
        return arr;
      }, []) || [];
    if (this.facets?.length && this.facets?.length <= 2) {
      this.isDefaultCollapsedFilter = false;
    } else {
      this.isDefaultCollapsedFilter = true;
    }
  }

  /**
   * in this function change facet lebel “Disciplines” to “Subject” for PreK-12 by checking facet code
   */
  private updateFacetLabel() {
    const updatedFacets = this.facets?.map((facet) => {
      if (facet.name === 'PRODUCT_TAG_PATH') {
        return { ...facet, label: 'Subject' };
      }
      return facet;
    });
    this.facets = updatedFacets;
  }

  openModal() {
    this.filterModal.open();
    emit({
      element: this,
      name: 'filter-open-modal',
      detail: this.analyticDetail(),
    });
  }

  clearAllFilters() {
    const state = store.getState() as StoreState;
    const searchString: string = searchParam(state)!;
    store.dispatch(
      performSearch({
        fieldValueFilter: {},
        searchTerms: searchString,
        start: 1,
        categoryId: this.categoryId,
      })
    );

    emit({
      element: this,
      name: 'filter-clear-all',
      detail: {
        ...this.analyticDetail(),
        'Refinement Type': 'Filter By',
      },
      dataLayer: true,
    });
    this.lastUpdatedFacet = null;
  }

  updateFacet(facet: string, value: SearchFacetValue, viaPill = false, isPerformSearch = true) {
    const state = store.getState() as StoreState;
    const searchString: string = searchParam(state)!;
    const val = value;
    let params = {};
    if (val.selected) {
      params = {
        removeFacetName: facet,
        removeFacetValue: val.name,
        excludeFacetName: facet,
        isPlpSearch: this.plpSearch,
        searchTerms: searchString,
        start: 1,
      };
    } else {
      params = {
        addFacetName: facet,
        addFacetValue: val.name,
        excludeFacetName: facet,
        isPlpSearch: this.plpSearch,
        searchTerms: searchString,
        start: 1,
        categoryId: this.categoryId,
      };
    }

    if (isPerformSearch) {
      store.dispatch(performSearch(params));
    } else {
      store.dispatch(setLoading(getSearchRequest(params, state)));
    }

    let analyticLogName = value.selected ? 'filter-remove-facet' : 'filter-add-facet';
    if (viaPill) {
      analyticLogName = 'filter-remove-facet-pill';
    }

    // Prepare the array of applied filters
    const appliedFilters = this.appliedFilter.map((filter) => ({
      group: filter.name,
      label: filter.label,
    }));

    emit({
      element: this,
      name: analyticLogName,
      detail: {
        ...this.analyticDetail({ group: facet, name: value.name, label: value.label }),
        'Refinement Type': 'Filter By',
        appliedFilters, // Add applied filters to the event detail
      },
      dataLayer: true,
    });

    this.showAriaInModal = true;
    this.lastUpdatedFacet = facet;
  }

  analyticDetail(obj = {}) {
    const state = store.getState() as StoreState;
    return {
      id: this.id,
      store: this.store,
      user: getUserDataForUserAnalytics(state),
      searchPageType: selectSearchPageType(state),
      catalog: this.selectedCatalog,
      ...obj,
    };
  }

  replaceSpaceWithinStr(val: string) {
    return val.replace(/\s/g, '_');
  }

  /** Set focus on results bar when skip filters is clicked or pressed */
  async skipFilterClick(e) {
    emit({
      element: this,
      name: 'skip-to-results',
      detail: {
        target: 'product-program-list-view',
      },
      dataLayer: true,
    });
    e.target.blur();
    await this.focusSearchResults();
  }

  private async focusSearchResults() {
    if (this.plpSearch) {
      let plpComponent = document.querySelector('mhecomm-plp-search-results');
      
      if (!plpComponent && this.getRootNode() instanceof ShadowRoot) {
        const parentRoot = this.getRootNode() as ShadowRoot;
        plpComponent = parentRoot.querySelector('mhecomm-plp-search-results');
      }
      
      if (!plpComponent?.shadowRoot) {
        return;
      }
      
      await customElements.whenDefined('mhecomm-plp-search-results');
      await (plpComponent as { updateComplete: Promise<unknown> }).updateComplete;
      
      const innerSearchResults = plpComponent.shadowRoot.querySelector('mhecomm-search-results');
      if (!innerSearchResults?.shadowRoot) {
        return;
      }
      
      await customElements.whenDefined('mhecomm-search-results');
      await (innerSearchResults as { updateComplete: Promise<unknown> }).updateComplete;
      
      const searchResultsDiv = innerSearchResults.shadowRoot.querySelector('#search-results');
      (searchResultsDiv as HTMLElement)?.focus();
    } else {
      const printCatalogComponent = document.querySelector('mhecomm-print-catalog-search-results');
      const searchResultsDiv = printCatalogComponent?.shadowRoot?.getElementById('search-results');
      searchResultsDiv?.focus();
    }
  }

  skipFilterToTabClick(e) {
    emit({
      element: this,
      name: 'skip-to-results-tab',
      detail: {
        target: 'results-tab'
      },
      dataLayer: true
    });
    e.target.blur();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-search-filters': SearchFilters;
  }
}
